import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { InputLabel, Select } from '@components/Common';
import { Tank } from '@api/queries/tanks';
import { Terminal } from '@api/queries/terminals';

const Column = styled.div`
  width: 200px;
`;

export interface TankFilterValues {
  terminalId?: string | null;
  tankId?: string | null;
}

interface Props {
  terminals: Terminal[];
  tanks: Tank[];
  value: TankFilterValues;
  onChange: (value: TankFilterValues) => void;
  isDisabled?: boolean;
  autoFocusTank?: boolean;
}

const TankSelectorControlled: React.FC<Props> = ({
  terminals,
  tanks,
  value,
  onChange,
  isDisabled = false,
  autoFocusTank = true,
}) => {
  const { t } = useTranslation();

  const { terminalId: stateTerminalId, tankId } = value;

  // Selected tank's terminal takes priority over terminalId from state
  const tank = tanks.find(x => x.id === tankId);
  const terminalId = tankId && tank ? tank.terminalId : stateTerminalId || null;

  let filteredTanks = tanks.filter(x => x.isEnabled || x.id === tankId);

  if (terminalId) {
    filteredTanks = filteredTanks.filter(x => x.terminalId === terminalId);
  }

  const tankOptions = filteredTanks.map(i => ({ value: i.id, label: i.name }));
  const terminalOptions = terminals.map(i => ({ value: i.id, label: i.name }));

  const selectedTerminal = terminalOptions.find(i => i.value === terminalId) || null;
  const selectedTank = tankOptions.find(i => i.value === tankId) || null;

  const handleTerminalChange = (newTerminalId: string | null) => {
    if (newTerminalId !== terminalId || (newTerminalId === null && tankId)) {
      onChange({
        terminalId: newTerminalId,
        tankId: null,
      });
    }
  };

  const handleTankChange = (newTankId: string | null) => {
    const newTank = tanks.find(t => t.id === newTankId);
    const oldTank = tanks.find(t => t.id === tankId);

    const newTerminalId = newTank
      ? newTank.terminalId
      : oldTank
        ? oldTank.terminalId
        : terminalId;

    onChange({
      terminalId: newTerminalId,
      tankId: newTankId,
    });
  };

  return (
    <>
      <Column className="column">
        <InputLabel htmlFor="terminalFilter">{t('entry.terminal')}</InputLabel>
        <Select
          name="terminalFilter"
          placeholder={t('select_terminal')}
          value={selectedTerminal}
          onChange={(i, j) => handleTerminalChange(j.action === 'clear' ? null : i.value)}
          options={terminalOptions}
          isDisabled={isDisabled}
        />
      </Column>
      <Column className="column">
        <InputLabel htmlFor="tankFilter">{t('entry.tank')}</InputLabel>
        <Select
          name="tankFilter"
          placeholder={t('entry.select_tank')}
          value={selectedTank}
          onChange={(i, j) => handleTankChange(j.action === 'clear' ? null : i.value)}
          options={tankOptions}
          isDisabled={isDisabled}
          autoFocus={autoFocusTank}
        />
      </Column>
    </>
  );
};

export default TankSelectorControlled;
